// shared/application/useImagenesDetalle.js  (imágenes del detalle: subida inmediata por miniatura + eliminación diferida con marcas). Sin JSX, sin Swal.
// Compartido por capacitaciones y visitas. `tipo`: 'capacitacion' | 'visita'. `entity` necesita `id` numérico y `media[]`.
// `setEntity(fn)` aplica `fn(entity) -> entity` sobre la entidad cargada del hook padre (sin efecto si aún no hay entidad).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mediaRepository } from '../infrastructure/media.repository';
import { archivoRepository } from '../infrastructure/archivo';
import { reintentarImagen } from './guardarConHijos';
import { validarImagenes } from './validarImagenes';
import { optimizarImagen } from './optimizarImagen';
import { compresorImagen } from '../infrastructure/compresorImagen';
import { ejecutarEliminaciones } from './ejecutarEliminaciones';
import { toApiDateTime } from '../domain/fecha';
import {
  conMarcaEliminacion,
  conProgresoEliminacion,
  mensajeErrorSubida,
  recortarAlLimite,
  sinImagenes,
  uuidsPorEliminar,
  uuidsConMarca,
  MAX_IMAGENES,
} from '../domain/media';
import { v4 as uuidv4 } from 'uuid';

export const useImagenesDetalle = ({
  tipo,
  entity,
  setEntity,
  media = mediaRepository,
  archivo = archivoRepository,
  compresor = compresorImagen,
} = {}) => {
  const [pendientes, setPendientesState] = useState([]);
  const pendientesRef = useRef([]);
  const [eliminando, setEliminando] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Espejo síncrono de `pendientes` (agregar/reintentar/confirmar son async y no deben leer un valor viejo).
  const setPendientes = useCallback(transformar => {
    pendientesRef.current = transformar(pendientesRef.current);
    if (mounted.current) setPendientesState(pendientesRef.current);
  }, []);

  // Imágenes vigentes: las guardadas que no están marcadas para eliminar.
  const totalActual = useMemo(() => {
    const lista = entity ? entity.media || [] : [];
    return lista.length - uuidsConMarca(lista).length;
  }, [entity]);

  // Total para el límite: guardadas vigentes + todas las pendientes (cualquier estado).
  const totalRef = useRef(0);
  totalRef.current = totalActual + pendientes.length;

  const marcar = useCallback(
    (uuid, estado, payload) =>
      setPendientes(lista =>
        lista.map(p =>
          p.uuid === uuid
            ? {
                ...p,
                estado,
                error: estado === 'error' ? mensajeErrorSubida(payload) : null,
                media: estado === 'done' ? payload || null : p.media,
              }
            : p
        )
      ),
    [setPendientes]
  );

  // Las marcas viven en `entity.media[].eliminacion` ({ estado: 'marked' | 'deleting' | 'error', error }), igual que en el editor del formulario.
  const actualizarMedia = useCallback(
    transformar => {
      if (!mounted.current) return;
      setEntity(actual => ({ ...actual, media: transformar(actual.media || []) }));
    },
    [setEntity]
  );

  /**
   * Valida, recorta al límite (MAX_IMAGENES, contando pendientes), agrega las aceptadas de inmediato como 'procesando',
   * las comprime una a una y luego las sube en secuencia (cada fallo queda en su miniatura). Resuelve al terminar el lote con
   * { rechazadas: [{ name, reason }], excedentes: File[], agregadas, fallidas }.
   */
  const agregarImagenes = useCallback(
    async files => {
      const { validos, rechazados } = await validarImagenes(files, archivo);
      const { aceptados, excedentes } = recortarAlLimite(validos, totalRef.current);
      const items = aceptados.map(file => ({ uuid: uuidv4(), file, estado: 'procesando', error: null, media: null }));
      const resultado = { rechazadas: rechazados, excedentes, agregadas: items.length, fallidas: 0 };
      if (!items.length || !entity) return { ...resultado, agregadas: 0 };

      totalRef.current += items.length;
      setPendientes(lista => [...lista, ...items]);

      const listos = [];
      const rechazadasProceso = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const item of items) {
        // eslint-disable-next-line no-await-in-loop
        const { file, rechazada } = await optimizarImagen(item.file, compresor);
        if (rechazada) {
          rechazadasProceso.push(rechazada);
          setPendientes(lista => lista.filter(p => p.uuid !== item.uuid));
        } else {
          listos.push({ ...item, file, estado: 'queued' });
          setPendientes(lista => lista.map(p => (p.uuid === item.uuid ? { ...p, file, estado: 'queued' } : p)));
        }
      }
      resultado.rechazadas = [...rechazados, ...rechazadasProceso];
      resultado.agregadas = listos.length;
      if (!listos.length) return resultado;

      const { fallidas } = await media.uploadEach({
        tipo,
        idPadre: entity.id,
        items: listos,
        creado: toApiDateTime(new Date()),
        onItem: (item, estado, payload) => marcar(item.uuid, estado, payload),
      });
      return { ...resultado, fallidas: fallidas.length };
    },
    [tipo, entity, media, archivo, compresor, marcar, setPendientes]
  );

  /** Reintenta UNA pendiente fallida contra el padre ya cargado. Devuelve true si se subió. */
  const reintentar = useCallback(
    async uuid => {
      const item = pendientesRef.current.find(p => p.uuid === uuid);
      if (!item || !entity || item.estado === 'uploading') return false;
      marcar(uuid, 'queued');
      const guardada = await reintentarImagen({
        tipo,
        idPadre: entity.id,
        item,
        media,
        creado: toApiDateTime(new Date()),
        onImagenEstado: marcar,
      });
      return !!guardada;
    },
    [tipo, entity, media, marcar]
  );

  /** Descarta una pendiente (p. ej. una que falló). */
  const quitarPendiente = useCallback(uuid => setPendientes(lista => lista.filter(p => p.uuid !== uuid)), [setPendientes]);

  /** Una pendiente 'done' ya está en el backend: pasa a la lista guardada local (la UI lo llama tras mostrar la marca de éxito). */
  const confirmarSubida = useCallback(
    uuid => {
      const item = pendientesRef.current.find(p => p.uuid === uuid);
      if (!item || item.estado !== 'done') return;
      const guardada = item.media && item.media.uuid ? item.media : { ...(item.media || {}), uuid: item.uuid };
      actualizarMedia(lista => (lista.some(m => m.uuid === guardada.uuid) ? lista : [...lista, guardada]));
      setPendientes(lista => lista.filter(p => p.uuid !== uuid));
    },
    [actualizarMedia, setPendientes]
  );

  const subiendoImagenes = pendientes.some(p => p.estado === 'procesando' || p.estado === 'queued' || p.estado === 'uploading');

  /** Marca una imagen guardada para eliminarla al aplicar (sin API). */
  const marcarParaEliminar = useCallback(mediaUuid => actualizarMedia(lista => conMarcaEliminacion(lista, mediaUuid)), [actualizarMedia]);

  /** Deshace la marca de una imagen. */
  const deshacerEliminar = useCallback(mediaUuid => actualizarMedia(lista => conMarcaEliminacion(lista, mediaUuid, null)), [
    actualizarMedia,
  ]);

  /** Descarta todas las marcas (Cancelar / Esc): no hay llamadas al API. */
  const descartarMarcas = useCallback(() => {
    actualizarMedia(lista => lista.map(m => (m.eliminacion ? { ...m, eliminacion: null } : m)));
  }, [actualizarMedia]);

  /** uuids marcadas (marked | error) que se eliminarán en la próxima aplicación. */
  const porEliminar = useMemo(() => uuidsPorEliminar(entity ? entity.media : []), [entity]);

  /**
   * Ejecuta DELETE secuencial de las marcadas (404 = éxito). Las exitosas quedan 'deleting' (la UI las desvanece y luego
   * llama `quitarImagenes`); las fallidas pasan a 'error' y siguen marcadas. Devuelve { eliminadas: [uuid], fallidas: [{ uuid, error }] }.
   */
  const eliminarMarcadas = useCallback(async () => {
    if (!entity) return { eliminadas: [], fallidas: [] };
    if (mounted.current) setEliminando(true);
    try {
      return await ejecutarEliminaciones({
        uuids: uuidsPorEliminar(entity.media),
        media,
        onEstado: (mediaUuid, estado, mensaje) => {
          if (estado !== 'done') actualizarMedia(lista => conProgresoEliminacion(lista, mediaUuid, estado, mensaje));
        },
      });
    } finally {
      if (mounted.current) setEliminando(false);
    }
  }, [entity, media, actualizarMedia]);

  /** Quita las imágenes del view model local (sin recargar); el contador `Imágenes (N)` sale de la lista. */
  const quitarImagenes = useCallback(uuids => actualizarMedia(lista => sinImagenes(lista, uuids)), [actualizarMedia]);

  return {
    agregarImagenes,
    totalImagenes: totalActual + pendientes.length,
    limiteAlcanzado: totalActual + pendientes.length >= MAX_IMAGENES,
    pendientes,
    subiendoImagenes,
    reintentar,
    quitarPendiente,
    confirmarSubida,
    eliminando,
    porEliminar,
    marcarParaEliminar,
    deshacerEliminar,
    descartarMarcas,
    eliminarMarcadas,
    quitarImagenes,
  };
};

export default useImagenesDetalle;
